import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import BlogContent from '../components/BlogContent';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useI18n } from '../i18n/I18nProvider';
import { I18nProvider } from '../i18n/I18nProvider';
import { DEFAULT_LOCALE } from '../i18n/config';
import { LEANPUB_URL } from '../lib/constants';
import { getBlogPostBySlug } from '../lib/blogPosts';
import { Calendar, Clock, ArrowLeft } from 'lucide-react';

const BlogPost = () => {
  return (
    <I18nProvider initialLocale={DEFAULT_LOCALE}>
      <BlogPostContent />
    </I18nProvider>
  );
};

const BlogPostContent = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useI18n();
  const post = slug ? getBlogPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <>
        <Helmet>
          <title>Artículo no encontrado - Angular Testing</title>
        </Helmet>
        <div className='min-h-screen flex items-center justify-center'>
          <div className='text-center'>
            <h1 className='text-4xl font-bold mb-4'>404</h1>
            <p className='text-xl text-gray-600 mb-4'>Artículo no encontrado</p>
            <Link to='/blog' className='text-primary hover:underline'>
              Volver al blog
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} - Angular Testing Blog</title>
        <meta name='description' content={post.description} />
        <meta name='keywords' content={post.keywords.join(', ')} />
        <meta property='og:title' content={post.title} />
        <meta property='og:description' content={post.description} />
        <meta property='og:type' content='article' />
        <html lang='es' />
        <link rel='canonical' href={`/blog/${post.slug}`} />
      </Helmet>

      <div className='min-h-screen flex flex-col'>
        <Header leanpubUrl={LEANPUB_URL} />
        <main className='flex-grow pt-16'>
          <article className='container mx-auto px-4 sm:px-6 lg:px-8 py-12'>
            <div className='max-w-4xl mx-auto'>
              <Link
                to='/blog'
                className='inline-flex items-center text-gray-600 hover:text-primary mb-6 transition-colors'
              >
                <ArrowLeft className='h-4 w-4 mr-2' />
                Volver al blog
              </Link>

              <header className='mb-8'>
                <div className='flex items-center gap-4 text-sm text-gray-500 mb-4'>
                  <div className='flex items-center gap-1'>
                    <Calendar className='h-4 w-4' />
                    <time dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </time>
                  </div>
                  <div className='flex items-center gap-1'>
                    <Clock className='h-4 w-4' />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                <h1 className='text-4xl md:text-5xl font-bold mb-4'>{post.title}</h1>
                <p className='text-xl text-gray-600 leading-relaxed'>{post.description}</p>

                <div className='flex flex-wrap gap-2 mt-6'>
                  {post.keywords.map((keyword) => (
                    <span
                      key={keyword}
                      className='px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full'
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </header>

              <div className='prose prose-lg max-w-none'>
                <div className='bg-white rounded-lg shadow-sm p-6 md:p-8'>
                  <BlogContent content={post.content} />
                </div>
              </div>

              <div className='mt-12 pt-8 border-t border-gray-200'>
                <Link
                  to='/blog'
                  className='inline-flex items-center text-primary hover:text-primary/80 font-medium transition-colors'
                >
                  <ArrowLeft className='h-4 w-4 mr-2' />
                  Ver todos los artículos
                </Link>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default BlogPost;
